import { Link } from 'react-router-dom'
import {Car, Shield, Clock, CheckCircle, Star, Phone, Mail, MapPin, Zap, Award} from 'lucide-react'
import AvailableVehicles from '@/components/AvailableVehicles'
import VehicleCategories from '@/components/VehicleCategories'

export default function HomePage() {
  return (
    <div className="bg-black">
      {/* Promo Banner */}
      <section className="bg-[#fbbf24] text-black py-3 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-black rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-black rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p className="text-sm md:text-base font-bold flex items-center justify-center gap-2">
            <Zap className="w-5 h-5" />
            Nouveau programme de fidélité disponible — Votre fidélité, enfin récompensée
          </p>
        </div>
      </section>

      {/* Hero Section - Full Image Background */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1600&auto=format&fit=crop&q=80" 
            alt="Car"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-transparent"></div>
          <div className="absolute inset-0 bg-[#fbbf24]/5"></div>
        </div>

        {/* Geometric Accents */}
        <div className="absolute top-20 right-10 w-32 h-32 border-4 border-[#fbbf24] rotate-12 opacity-20"></div>
        <div className="absolute bottom-32 right-32 w-24 h-24 bg-[#fbbf24] opacity-10 rounded-full blur-2xl"></div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-20">
          <div className="max-w-2xl">
            <div className="inline-block mb-6">
              <span className="bg-[#fbbf24] text-black px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                Bussigny • Suisse
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-none text-white">
              Location de
              <span className="block text-[#fbbf24] mt-2">véhicules</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed">
              Réservez en ligne en <span className="text-[#fbbf24] font-semibold">3 minutes</span>. 
              Large gamme de véhicules disponibles immédiatement.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/flotte"
                className="group relative bg-[#fbbf24] text-black px-8 py-5 rounded-xl font-bold text-lg hover:bg-[#f59e0b] transition-all overflow-hidden text-center"
              >
                <span className="relative z-10">Voir notre flotte</span>
                <div className="absolute inset-0 bg-black/10 translate-y-full group-hover:translate-y-0 transition-transform"></div>
              </Link>
              <Link
                to="/flotte"
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-5 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-[#fbbf24] transition-all text-center"
              >
                Réserver maintenant
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/10">
              <div>
                <p className="text-3xl font-bold text-[#fbbf24]">20+</p>
                <p className="text-gray-400 text-sm">Véhicules</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#fbbf24]">1500+</p>
                <p className="text-gray-400 text-sm">Clients satisfaits</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#fbbf24]">4.9/5</p>
                <p className="text-gray-400 text-sm">Note Google</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Vehicles Section */}
      <AvailableVehicles />

      {/* Vehicle Categories Section */}
      <VehicleCategories />

      {/* Features Section - Diagonal Split */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Diagonal Background Split */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-white"></div>
          <div className="absolute inset-0 bg-[#fbbf24] transform origin-top-left -skew-y-3"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-black mb-6">
              Pourquoi choisir 
              <span className="block text-black mt-2">ADY Garage ?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Feature 1 */}
            <div className="group bg-black p-8 rounded-2xl hover:bg-[#1a1a1a] transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border border-[#fbbf24]/20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fbbf24] rounded-xl mb-6 group-hover:scale-110 transition-transform">
                <Car className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Large choix
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Voitures, utilitaires et véhicules spécialisés disponibles
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group bg-black p-8 rounded-2xl hover:bg-[#1a1a1a] transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border border-[#fbbf24]/20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fbbf24] rounded-xl mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Assurance complète
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Protection optimale pour une location en toute sérénité
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group bg-black p-8 rounded-2xl hover:bg-[#1a1a1a] transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border border-[#fbbf24]/20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fbbf24] rounded-xl mb-6 group-hover:scale-110 transition-transform">
                <Clock className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Disponibilité 24/7
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Réservez en ligne à tout moment selon vos disponibilités
              </p>
            </div>

            {/* Feature 4 */}
            <div className="group bg-black p-8 rounded-2xl hover:bg-[#1a1a1a] transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border border-[#fbbf24]/20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fbbf24] rounded-xl mb-6 group-hover:scale-110 transition-transform">
                <CheckCircle className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Réservation simple
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Processus de réservation rapide et confirmation instantanée
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section - Modern Cards with Images */}
      <section className="py-20 md:py-32 bg-white relative">
        {/* Decorative Elements */}
        <div className="absolute top-20 left-0 w-72 h-72 bg-[#fbbf24]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-[#fbbf24]/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#fbbf24] text-black px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-4">
              Simple & Rapide
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-black mb-6">
              Comment ça marche ?
            </h2>
            <p className="text-gray-600 text-xl max-w-2xl mx-auto">
              Réservez votre véhicule en 3 étapes simples
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#fbbf24]">
              <div className="absolute -top-6 left-8">
                <div className="w-14 h-14 bg-[#fbbf24] text-black rounded-2xl font-black text-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  1
                </div>
              </div>
              <div className="mt-6 mb-6">
                <div className="w-full h-48 bg-gradient-to-br from-[#fbbf24]/10 to-[#fbbf24]/5 rounded-2xl flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&auto=format&fit=crop&q=80" 
                    alt="Choose vehicle"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">
                Choisissez votre véhicule
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Parcourez notre flotte et sélectionnez le véhicule qui correspond à vos besoins
              </p>
            </div>

            {/* Step 2 */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#fbbf24] md:mt-12">
              <div className="absolute -top-6 left-8">
                <div className="w-14 h-14 bg-[#fbbf24] text-black rounded-2xl font-black text-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  2
                </div>
              </div>
              <div className="mt-6 mb-6">
                <div className="w-full h-48 bg-gradient-to-br from-[#fbbf24]/10 to-[#fbbf24]/5 rounded-2xl flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&auto=format&fit=crop&q=80" 
                    alt="Select dates"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">
                Sélectionnez vos dates
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Choisissez les dates et heures de retrait et de retour du véhicule
              </p>
            </div>

            {/* Step 3 */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#fbbf24]">
              <div className="absolute -top-6 left-8">
                <div className="w-14 h-14 bg-[#fbbf24] text-black rounded-2xl font-black text-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  3
                </div>
              </div>
              <div className="mt-6 mb-6">
                <div className="w-full h-48 bg-gradient-to-br from-[#fbbf24]/10 to-[#fbbf24]/5 rounded-2xl flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&auto=format&fit=crop&q=80" 
                    alt="Confirm"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">
                Confirmez et récupérez
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Complétez votre réservation en ligne et récupérez votre véhicule au moment convenu
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Split Section - Bold Yellow/Black */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[500px]">
          {/* Left - Yellow Side */}
          <div className="bg-[#fbbf24] relative overflow-hidden flex items-center">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-20 -right-20 w-96 h-96 bg-black rounded-full blur-3xl"></div>
            </div>
            <div className="relative z-10 p-12 md:p-16 w-full">
              <Award className="w-16 h-16 text-black mb-6" />
              <h2 className="text-4xl md:text-5xl font-black text-black mb-6 leading-tight">
                Programme de fidélité
              </h2>
              <p className="text-black/80 text-lg mb-8 leading-relaxed">
                Gagnez des points à chaque location et bénéficiez d'avantages exclusifs
              </p>
              <Link
                to="/flotte"
                className="inline-block bg-black text-[#fbbf24] px-8 py-4 rounded-xl font-bold hover:bg-black/90 transition-all hover:scale-105"
              >
                En savoir plus
              </Link>
            </div>
          </div>

          {/* Right - Black Side with Image */}
          <div className="bg-black relative overflow-hidden flex items-center">
            <div className="absolute inset-0">
              <img 
                src="https://images.unsplash.com/photo-1550355291-bbee04a92027?w=1000&auto=format&fit=crop&q=80" 
                alt="Premium cars"
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
            </div>
            <div className="relative z-10 p-12 md:p-16 w-full">
              <Zap className="w-16 h-16 text-[#fbbf24] mb-6" />
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                Prêt en 
                <span className="text-[#fbbf24]"> 24h</span>
              </h2>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                Réservation instantanée, véhicule prêt rapidement. Service express disponible.
              </p>
              <Link
                to="/flotte"
                className="inline-block bg-[#fbbf24] text-black px-8 py-4 rounded-xl font-bold hover:bg-[#f59e0b] transition-all hover:scale-105"
              >
                Réserver maintenant
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Bold Cards */}
      <section className="py-20 md:py-32 bg-white relative">
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent opacity-5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block bg-black text-[#fbbf24] px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-4">
              Témoignages
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-black mb-6">
              Ce que disent nos clients
            </h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-[#fbbf24] text-[#fbbf24]" />
              ))}
            </div>
            <p className="text-gray-600 text-lg font-semibold">4.9/5 — 31 avis Google</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Testimonial 1 */}
            <div className="group relative bg-black p-8 rounded-3xl hover:shadow-2xl transition-all duration-300 border-2 border-[#fbbf24]">
              <div className="absolute -top-6 -left-6 text-[#fbbf24] text-8xl font-black opacity-20">"</div>
              <div className="flex gap-1 mb-4 relative z-10">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#fbbf24] text-[#fbbf24]" />
                ))}
              </div>
              <p className="text-white text-lg mb-6 relative z-10 leading-relaxed">
                Merciiii!!! Rapide, sympa, agréable et de très bon conseil !!! Je vous recommande vivement⭐⭐⭐⭐⭐
              </p>
              <p className="text-[#fbbf24] font-bold relative z-10">— JUSTE LAUSANNE</p>
            </div>

            {/* Testimonial 2 */}
            <div className="group relative bg-black p-8 rounded-3xl hover:shadow-2xl transition-all duration-300 border-2 border-[#fbbf24]">
              <div className="absolute -top-6 -left-6 text-[#fbbf24] text-8xl font-black opacity-20">"</div>
              <div className="flex gap-1 mb-4 relative z-10">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#fbbf24] text-[#fbbf24]" />
                ))}
              </div>
              <p className="text-white text-lg mb-6 relative z-10 leading-relaxed">
                Excellent! Highly recommended.
              </p>
              <p className="text-[#fbbf24] font-bold relative z-10">— Nenad Savic</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - Asymmetric Layout */}
      <section id="contact" className="py-20 md:py-32 bg-black relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#fbbf24] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#fbbf24] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Contact Info */}
            <div>
              <span className="inline-block bg-[#fbbf24] text-black px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-6">
                Contactez-nous
              </span>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                Une question ?
                <span className="block text-[#fbbf24]">On vous répond !</span>
              </h2>
              <p className="text-gray-400 text-lg mb-10 leading-relaxed">
                Notre équipe est disponible pour vous conseiller et répondre à toutes vos questions
              </p>

              <div className="space-y-6">
                <a
                  href="tel:+41217062626"
                  className="group flex items-center gap-5 bg-white/5 backdrop-blur-sm p-6 rounded-2xl hover:bg-[#fbbf24] transition-all duration-300 border border-white/10 hover:border-[#fbbf24]"
                >
                  <div className="w-14 h-14 bg-[#fbbf24] group-hover:bg-black rounded-xl flex items-center justify-center transition-colors flex-shrink-0">
                    <Phone className="w-7 h-7 text-black group-hover:text-[#fbbf24]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 group-hover:text-black/70 mb-1">Téléphone</p>
                    <p className="font-bold text-white group-hover:text-black text-xl">021 706 26 26</p>
                  </div>
                </a>

                <a
                  href="mailto:info@adysarl.ch"
                  className="group flex items-center gap-5 bg-white/5 backdrop-blur-sm p-6 rounded-2xl hover:bg-[#fbbf24] transition-all duration-300 border border-white/10 hover:border-[#fbbf24]"
                >
                  <div className="w-14 h-14 bg-[#fbbf24] group-hover:bg-black rounded-xl flex items-center justify-center transition-colors flex-shrink-0">
                    <Mail className="w-7 h-7 text-black group-hover:text-[#fbbf24]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 group-hover:text-black/70 mb-1">Email</p>
                    <p className="font-bold text-white group-hover:text-black text-xl">info@adysarl.ch</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/41795705456"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-5 bg-white/5 backdrop-blur-sm p-6 rounded-2xl hover:bg-[#25D366] transition-all duration-300 border border-white/10 hover:border-[#25D366]"
                >
                  <div className="w-14 h-14 bg-[#25D366] group-hover:bg-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0">
                    <svg className="w-7 h-7 text-white group-hover:text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 group-hover:text-white/70 mb-1">WhatsApp</p>
                    <p className="font-bold text-white group-hover:text-white text-xl">079 570 54 56</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative h-full min-h-[400px]">
              <img 
                src="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1000&auto=format&fit=crop&q=80" 
                alt="Contact"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Map & Location - Modern Card */}
      <section className="py-20 md:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-black text-[#fbbf24] px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-4">
              Notre garage
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-black mb-6">
              Où nous trouver
            </h2>
          </div>

          <div className="relative bg-black rounded-3xl overflow-hidden shadow-2xl border-4 border-[#fbbf24]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2747.235824373!2d6.537822!3d46.546849!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c2f5e6a4f1111%3A0x1234567890abcdef!2sRue%20de%20l'Industrie%2049%2C%201030%20Bussigny!5e0!3m2!1sfr!2sch!4v1234567890123"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ady Garage Bussigny"
            />
          </div>

          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-3 bg-black text-white px-8 py-5 rounded-2xl shadow-lg">
              <MapPin className="w-6 h-6 text-[#fbbf24]" />
              <span className="font-bold text-lg">Rue de l'Industrie 49, 1030 Bussigny</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
